import {useCallback, useRef} from 'react';

/**
 * Allows queueing up multiple actions, where the latter action waits for the previous one to finish. In addition, in case of multiple actions queued, intermediate ones will be skipped over, for example:
 *  1. Call Action1
 *  2. Queue Action2
 *  3. Queue Action3
 *  4. Action1 completes
 *  5. Call Action3 (note how Action2 was never called)
 *
 * It's expected that the callback returns a promise - the promise value will be passed as an argument to the callback. When there's no in-progress actions, `initialArg` will be passed instead.
 */
export function usePersistQueue() {
    const persistQueue = useRef({});

    return useCallback((bucket, callback, initialArg) => {
        queueAction(persistQueue.current, bucket, callback, initialArg);
    }, []);
}

function queueAction(queue, bucket, callback, initialArg) {
    if (bucket in queue) {
        // Assume any already-queued action is no longer relevant
        queue[bucket] = callback;
    } else {
        queue[bucket] = pendingTag;
        callback(initialArg).then(returnValue => {
            const nextCallback = queue[bucket];
            delete queue[bucket];
            if (nextCallback && nextCallback !== pendingTag) {
                queueAction(queue, bucket, nextCallback, returnValue);
            }
        }).catch(() => {
            // Error might be a temporary issue. Continue to the next queued action as if nothing has happened.
            const nextCallback = queue[bucket];
            delete queue[bucket];
            if (nextCallback && nextCallback !== pendingTag) {
                queueAction(queue, bucket, nextCallback, initialArg);
            }
        });
    }
}

function pendingTag() {
    // Indicates that there's an action in progress and there's no next action after that.
}
